import React, { useRef, useState } from "react";
import { CellProps, Column } from "react-datasheet-grid";
import { downloadFile, uploadImage } from "@hooks/utils/useUpload";
import { ToastOptions, toast } from "react-toastify";
import { Box, Button } from "@mui/material";
import UploadTwoToneIcon from "@mui/icons-material/UploadTwoTone";
import DownloadTwoToneIcon from "@mui/icons-material/DownloadTwoTone";
import { ro } from "date-fns/locale";
import { CircularProgressWithLabel } from "@stories/molecules/CircularProgressWithLabel/CircularProgressWithLabel";

export const SheetButton = React.memo(
  ({
    active,
    rowData,
    setRowData,
    focus,
    stopEditing,
    columnData,
  }: CellProps<string | null>) => {
    const fileInput = useRef<HTMLInputElement | null>(null);

    const [uploadProgress, setUploadProgress] = useState(0);

    const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      setUploadProgress(0);
      if (e.currentTarget?.files && e.currentTarget?.files.length > 0) {
        const file = e.currentTarget?.files[0] || null;
        if (file) {
          columnData.setIsUploading(true);

          const fileKey = `invoices/${columnData.projectId}/${file.name}`;
          await uploadImage(
            fileKey,
            file,
            (progress: any) => {
              const total = (progress.loaded / progress.total) * 100;
              setUploadProgress(total);
              if (total === 100) {
                toast("Upload complete!", {
                  type: "success",
                } as ToastOptions);
                setUploadProgress(0);
                columnData.setIsUploading(false);
                stopEditing({ nextRow: false });
              }
            },
            (error: any) => {
              setUploadProgress(0);
              console.log(error);
              toast("Upload failed - something went wrong!", {
                type: "error",
              } as ToastOptions);
              columnData.setIsUploading(false);
              stopEditing({ nextRow: false });
            }
          );
          setRowData(fileKey);
        }
      }
    };

    const onFileInput = () => {
      if (fileInput.current) {
        fileInput.current.click();
      }
    };
    const onFileDownload = () => {
      if (rowData) {
        // eslint-disable-next-line no-useless-escape
        const fileName = rowData.replace(/^.*[\\\/]/, "");
        downloadFile(rowData, fileName);
      }
    };
    // const currentValue = columnData.options.filter((o) =>
    //   rowData?.includes(o.value)
    // );
    return (
      <Box sx={{ grow: 1, margin: "auto" }}>
        {uploadProgress === 0 ? (
          <label htmlFor="containedButtonFile">
            <input
              ref={fileInput}
              hidden
              accept="image/*"
              type="file"
              style={{
                opacity: 0,
                position: "fixed",
                zIndex: 1,
                padding: 0.5,
                cursor: "pointer",
                width: "100px",
                height: "100px",
              }}
              onChange={onUpload}
            />
            <Button
              variant="outlined"
              size="small"
              sx={{
                fontSize: "0.5rem",
              }}
              disabled={columnData.isUploading}
              startIcon={
                !rowData || rowData.length === 0 ? (
                  <UploadTwoToneIcon />
                ) : (
                  <DownloadTwoToneIcon />
                )
              }
              onClick={
                !rowData || rowData.length === 0 ? onFileInput : onFileDownload
              }
            >
              {!rowData || rowData.length === 0 ? "Upload" : "Download"}
            </Button>
          </label>
        ) : (
          <CircularProgressWithLabel value={uploadProgress} />
        )}
      </Box>
    );
  }
);

export const buttonColumn = (
  projectId: string,
  isUploading: boolean,
  setIsUploading: (toggle: boolean) => void
): Column<string | null> => ({
  component: SheetButton as any,
  columnData: {
    projectId,
    isUploading,
    setIsUploading,
  },
});
